import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { me, pollTelegramBotLogin, startTelegramBotLogin, type User } from '../lib/auth-client';
import { failureEvent, failureReason, useAuthOptional, type AuthIntent } from '../lib/auth-context';
import { track } from '../lib/funnel';
import { uuid } from '../lib/uuid';
import { watchHandoff, type InAppBrowser } from '../lib/in-app-browser';
import {
  IN_APP_HINT_MS,
  PREPARED_MIN_LIFE_MS,
  PREPARED_REFRESH_MS,
  REQUEST_TTL_MS,
  botHandoff,
  botHandoffDeadlineMs,
  claimBotLoginWait,
  clearPendingBotLogin,
  dropPreparedBotLogin,
  isDesktopHandoff,
  peekPreparedBotLogin,
  preparedIsStale,
  putPreparedBotLogin,
  readPendingBotLogin,
  releaseBotLoginWait,
  savePendingBotLogin,
  telegramBotLink,
  type BotLoginWaitOwner,
  type PendingBotLogin,
  type PreparedBotLogin,
} from '../lib/telegram-bot-login';
import { runBotLoginWait } from '../lib/telegram-bot-login-wait';
import { useLocaleOr } from '../i18n/locale-context';
import { DEFAULT_LOCALE, translate } from '../i18n/index';
import { TelegramIcon } from './icons';

/**
 * Which button this is — on every funnel row (`surface`), and the line between
 * SIGNING IN (`login`, `register`) and CONNECTING Telegram to the account this
 * browser is already signed into (everything else: the onboarding modal and
 * strip, the dashboard card, the catalog banner, checkout's identify step).
 */
export type TelegramBotSurface =
  | 'login'
  | 'register'
  | 'checkout'
  | 'banner'
  | 'onboarding'
  | 'onboardingStrip'
  | 'alerts';

/** What a host receives once the SERVER has signed this browser in (or linked
 *  Telegram to the session it already had). */
export interface TelegramBotAuthResult {
  isNew: boolean;
  /** True when Telegram was attached to the session this browser already had. */
  linked: boolean;
  user: User;
  /** The id this browser's Pixel leg of CompleteRegistration must carry — the
   *  server's CAPI leg already went out with it. */
  metaEventId: string;
}

interface Props {
  botUsername: string;
  surface: TelegramBotSurface;
  /** Which funnel a failure is filed under — `register_failed` on /register. */
  intent?: AuthIntent;
  inApp: InAppBrowser;
  onAuth: (result: TelegramBotAuthResult) => void | Promise<void>;
  /** A refusal the host should explain (409 `telegram_account_taken`, …). */
  onError?: (err: unknown) => void;
  /**
   * Where the bot's magic link («Перейти на сайт и настроить уведомления»)
   * lands after the sign-in — a same-site path, locale prefix included. The
   * sign-in pages pass the page the visitor came from; anywhere else the
   * default is the page this button is on.
   */
  returnTo?: string;
  /** The host's own label (the connect surfaces' «Подключить Telegram»). */
  children?: ReactNode;
  className?: string;
}

type Phase =
  /** The button is (or is about to be) ready to hand off. */
  | { kind: 'ready' }
  /** Tapped: waiting for Start in Telegram. `hint` = the page is still on
   *  screen after the tap (login/register), so «Не открылся Telegram?» shows.
   *  `restored` = picked back up from the pending cookie on this page load. */
  | { kind: 'waiting'; pending: PendingBotLogin; hint: boolean; restored: boolean }
  /** Signed in; the host is adopting the session. */
  | { kind: 'completing' }
  /** Back to the button, with a line saying why. */
  | { kind: 'notice'; notice: 'expired' | 'failed' };

type Prepared =
  | { status: 'preparing' }
  | { status: 'ready'; req: PreparedBotLogin }
  | { status: 'failed' };

/** The poll a wait restored from the pending cookie makes — `via: cookie`. */
const pollResumed = (token: string) => pollTelegramBotLogin(token, { resumed: true });

function currentPath(): string | undefined {
  if (typeof window === 'undefined') return undefined;
  return `${window.location.pathname}${window.location.search}`;
}

/**
 * Open the bot-login request BEFORE the tap, so the tap itself can hand off —
 * and keep it fresh while the page stays open.
 *
 * An in-app WebView drops a navigation that is not inside the user's gesture,
 * silently (the lesson of `use-prepared-checkout.ts`). Creating the request on
 * the tap and navigating after `await` would be exactly that.
 *
 * The server accepts a Start for fifteen minutes, so a request is renewed
 * every ~10 minutes and on every return to the tab once it is that old
 * (owner, 18.10): the button never carries a link the bot would call expired.
 */
function usePreparedBotLogin(
  enabled: boolean,
  locale: string,
  key: string,
  returnTo: string | undefined,
  surface: TelegramBotSurface,
  onFail: (err: unknown) => void,
) {
  const cacheKey = `${key}|${returnTo ?? ''}`;
  const [state, setState] = useState<Prepared>(() => {
    const hit = peekPreparedBotLogin(cacheKey);
    return hit ? { status: 'ready', req: hit } : { status: 'preparing' };
  });
  const [generation, setGeneration] = useState(0);
  const onFailRef = useRef(onFail);
  onFailRef.current = onFail;

  useEffect(() => {
    if (!enabled) return;
    const hit = peekPreparedBotLogin(cacheKey);
    if (hit) {
      setState({ status: 'ready', req: hit });
      return;
    }
    let cancelled = false;
    setState({ status: 'preparing' });
    const metaEventId = uuid();
    startTelegramBotLogin({ locale, metaEventId, returnTo, surface }).then(
      (r) => {
        const now = Date.now();
        const req: PreparedBotLogin = {
          token: r.token,
          startParam: r.startParam,
          expiresAt: now + r.expiresInS * 1000,
          metaEventId,
          preparedAt: now,
        };
        // Cached even if this effect was torn down: the request EXISTS
        // server-side, and a remount should use it rather than open another.
        putPreparedBotLogin(cacheKey, req);
        if (!cancelled) setState({ status: 'ready', req });
      },
      (err: unknown) => {
        if (cancelled) return;
        setState({ status: 'failed' });
        onFailRef.current(err);
      },
    );
    return () => {
      cancelled = true;
    };
  }, [enabled, locale, cacheKey, returnTo, surface, generation]);

  const renew = useCallback(() => {
    dropPreparedBotLogin(cacheKey);
    setGeneration((g) => g + 1);
  }, [cacheKey]);

  // A page left open renews its request: on a timer, and on a return to the
  // tab (a phone that slept froze the timer) once the request is due.
  useEffect(() => {
    if (!enabled || state.status !== 'ready') return;
    const req = state.req;
    const due = Math.min(
      req.preparedAt + PREPARED_REFRESH_MS,
      req.expiresAt - PREPARED_MIN_LIFE_MS,
    );
    const timer = window.setTimeout(renew, Math.max(0, due - Date.now()));
    const onReturn = () => {
      if (document.visibilityState === 'visible' && preparedIsStale(req)) renew();
    };
    document.addEventListener('visibilitychange', onReturn);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener('visibilitychange', onReturn);
    };
  }, [enabled, state, renew]);

  /** Hand the prepared request to a tap — once. */
  const take = (): PreparedBotLogin | null => {
    if (state.status !== 'ready') return null;
    if (state.req.expiresAt - Date.now() < PREPARED_MIN_LIFE_MS) {
      // A background timer can run late; never hand out a nearly dead link.
      renew();
      return null;
    }
    dropPreparedBotLogin(cacheKey);
    setState({ status: 'preparing' });
    return state.req;
  };

  return { state, take, renew };
}

/** «12:34» until `until`, ticking each second; null when there is nothing to count. */
function useCountdown(until: number | null): string | null {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (until === null) return;
    setNow(Date.now());
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [until]);
  if (until === null) return null;
  const left = Math.max(0, Math.ceil((until - now) / 1000));
  return `${Math.floor(left / 60)}:${String(left % 60).padStart(2, '0')}`;
}

/** Put `text` on the clipboard; true when it got there. */
async function copyText(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    /* refused (an in-app WebView, a permission) — try the old way */
  }
  try {
    const area = document.createElement('textarea');
    area.value = text;
    area.setAttribute('readonly', '');
    area.style.position = 'fixed';
    area.style.opacity = '0';
    document.body.appendChild(area);
    area.select();
    const ok = document.execCommand('copy');
    area.remove();
    return ok;
  } catch {
    return false;
  }
}

/**
 * «Войти через Telegram» / «Подключить Telegram» THROUGH THE BOT — since
 * 2026-10-18 the only Telegram door on the site (owner): /login and /register
 * sign in with it, and `TelegramConnectButton` wraps it on every connect
 * surface, where Start in the bot links Telegram to the signed-in account.
 *
 * 1. A request is opened in the background as the button mounts
 *    (`POST /auth/telegram/bot/start`), which also binds it to this browser
 *    with an httpOnly cookie, and renewed every ~10 minutes while the page is
 *    open.
 * 2. The tap hands off (`botHandoff`): inside an Android in-app browser the
 *    pinned Telegram intent with `t.me` as its fallback (the verified path);
 *    everywhere else `https://t.me/<bot>?start=login_<token>`, never `tg://` —
 *    in this same page on a phone (inside the gesture, so a WebView cannot
 *    drop it), in a new tab on a computer. The link is never put in the DOM:
 *    Meta's Pixel reports a clicked `<a>`'s href, PostHog autocapture records
 *    it, and it carries a login token.
 * 3. Start in the bot IS the sign-in. The bot answers «Готово, <имя>! Вы вошли
 *    на uztender.com.» with a one-time link for any other browser.
 * 4. This page polls (`POST /auth/telegram/bot/status`: ~2 s, backing off,
 *    paused while hidden, asked again the moment the page is visible) and the
 *    server signs THIS browser in — same login/link rules as the widget.
 *
 * The tapped request also goes into a short-lived first-party cookie
 * (`savePendingBotLogin`), so `TelegramBotLoginResume`, mounted once at the app
 * root, collects it when this surface is gone — a route change, or a tab that
 * was closed and a site opened again in the same browser.
 */
export function TelegramBotLogin({
  botUsername,
  surface,
  intent,
  inApp,
  onAuth,
  onError,
  returnTo,
  children,
  className,
}: Props) {
  const locale = useLocaleOr(DEFAULT_LOCALE);
  const t = (key: string, params?: Record<string, string>) => translate(locale, key, params);
  const auth = useAuthOptional();
  const userKey = auth?.user?.id ?? 'guest';
  const refreshRef = useRef(auth?.refresh);
  refreshRef.current = auth?.refresh;
  const onAuthRef = useRef(onAuth);
  onAuthRef.current = onAuth;
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;
  // Read once: the default is the page the button was mounted on.
  const [target] = useState(() => returnTo ?? currentPath());
  const authSurface = surface === 'login' || surface === 'register';
  const desktop = isDesktopHandoff(inApp);

  // A tapped request survives this page being closed and opened again.
  const [phase, setPhase] = useState<Phase>(() => {
    const pending = readPendingBotLogin(surface);
    // Back on a phone with the request still open: the hand-off may have been
    // the very thing that replaced this page (a WebView that loaded t.me
    // itself), so the way out is on screen from the start.
    return pending
      ? { kind: 'waiting', pending, hint: authSurface && !desktop, restored: true }
      : { kind: 'ready' };
  });
  const [copied, setCopied] = useState(false);

  /** Every way the bot flow ends WITHOUT a session, on one funnel event. */
  const reportFailure = (reason: string, extra: Record<string, unknown> = {}) =>
    track(failureEvent(intent), { method: 'telegram', via: 'bot', reason, surface, ...extra });

  const prepared = usePreparedBotLogin(
    phase.kind === 'ready' || phase.kind === 'notice',
    locale,
    userKey,
    returnTo ?? target,
    surface,
    (err) => reportFailure(failureReason(err), { step: 'prepare' }),
  );

  const watchRef = useRef<Array<() => void>>([]);
  const cancelWatch = () => {
    for (const stop of watchRef.current) stop();
    watchRef.current = [];
  };
  useEffect(() => cancelWatch, []);

  /** Leave for Telegram. Synchronous from the tap down to the navigation. */
  const handOff = (pending: PendingBotLogin) => {
    const h = botHandoff(inApp, botUsername, pending.startParam);
    cancelWatch();
    setCopied(false);
    if (h.kind === 'same') {
      // Armed BEFORE navigating, so an app switch that blurs the page at once
      // is not missed. A page that never goes away means the link did not
      // reach Telegram — or, inside Instagram, the person is still reading the
      // app's own «Вы покидаете приложение?», which keeps this page visible.
      // Two watchers: the funnel row waits as long as it always has
      // (`deadlineMs` rides on it, so the series stays comparable); the
      // on-screen way out on /login and /register comes after ~2 s (owner).
      const deadlineMs = botHandoffDeadlineMs(inApp);
      watchRef.current.push(
        watchHandoff(() => {
          reportFailure('intent_ignored', {
            step: 'handoff',
            client: true,
            deadlineMs,
            pageHidden: false,
          });
        }, deadlineMs),
      );
      if (authSurface) {
        watchRef.current.push(
          watchHandoff(() => {
            setPhase((p) => (p.kind === 'waiting' ? { ...p, hint: true } : p));
          }, IN_APP_HINT_MS),
        );
      }
      window.location.href = h.url;
      return;
    }
    const opened = window.open(h.url, '_blank');
    if (opened) {
      try {
        opened.opener = null;
      } catch {
        /* cross-origin already — nothing to sever */
      }
    } else {
      window.location.href = h.url;
    }
  };

  const onTap = () => {
    if (phase.kind === 'completing') return;
    if (phase.kind === 'waiting') {
      // «Открыть Telegram ещё раз» — the SAME request; a second Start is
      // harmless (the bot answers once).
      handOff(phase.pending);
      return;
    }
    if (prepared.state.status === 'failed') {
      prepared.renew();
      return;
    }
    const req = prepared.take();
    if (!req) return;
    const pending: PendingBotLogin = {
      token: req.token,
      startParam: req.startParam,
      expiresAt: req.expiresAt,
      metaEventId: req.metaEventId,
      surface,
      startedAt: Date.now(),
      ...(intent ? { intent } : {}),
    };
    // `device` and `inApp` ride on every beacon row (`funnelContext`).
    track('tg_login_tapped', { surface, mode: authSurface ? 'login' : 'link' });
    savePendingBotLogin(pending);
    setPhase({ kind: 'waiting', pending, hint: false, restored: false });
    handOff(pending);
  };

  const copyLink = async (pending: PendingBotLogin) => {
    if (await copyText(telegramBotLink(botUsername, pending.startParam))) setCopied(true);
  };

  // ── The wait ────────────────────────────────────────────────────────────
  const pending = phase.kind === 'waiting' ? phase.pending : null;
  const restored = phase.kind === 'waiting' && phase.restored;
  useEffect(() => {
    if (!pending) return;
    // One loop per request (the exchange is single-use). The tapping surface
    // outranks the app-wide resumer, which steps aside if it had picked this
    // request up while the surface was unmounted. Another mounted copy of THIS
    // surface already polling it is the only thing that refuses us.
    const owner: BotLoginWaitOwner = { kind: 'surface' };
    if (!claimBotLoginWait(pending.token, owner)) return;

    const end = (next: Phase) => {
      clearPendingBotLogin(pending.token);
      cancelWatch();
      setPhase(next);
    };

    const refused = (err: unknown) => {
      reportFailure(failureReason(err));
      clearPendingBotLogin(pending.token);
      cancelWatch();
      if (onErrorRef.current) {
        onErrorRef.current(err);
        setPhase({ kind: 'ready' });
      } else {
        setPhase({ kind: 'notice', notice: 'failed' });
      }
    };

    const stop = runBotLoginWait(
      pending,
      restored ? pollResumed : pollTelegramBotLogin,
      {
        signedIn: (st) => {
          end({ kind: 'completing' });
          void Promise.resolve()
            .then(() =>
              onAuthRef.current({
                isNew: st.isNew,
                linked: st.linked,
                user: st.user,
                metaEventId: pending.metaEventId,
              }),
            )
            // The host could not adopt the session (a failed /auth/me, say).
            // The server side is done; say so the way a refusal would be said.
            .catch((err: unknown) => refused(err));
        },
        refused,
        ended: (how) => {
          if (how === 'consumed') {
            // This browser already collected it — another tab, or the app
            // root. The session cookie is set: re-read it, and the host's
            // «already signed in» path takes over. Not a failure.
            end({ kind: 'ready' });
            void refreshRef.current?.().catch(() => undefined);
            return;
          }
          if (how === 'invalid') {
            // The server will not match this browser to the request: the nonce
            // cookie is gone or replaced. Measured 2026-10-19 (5 of 8 taps that
            // afternoon): either the person pressed the bot's «Перейти на сайт»
            // button in THIS browser — the magic link signed it in and rewrote
            // the cookie — or the tab outlived the fifteen-minute cookie. Both
            // were shown as «Не получилось — нажмите ещё раз». Ask the session
            // first; a request past its own lifetime is `expired`, not a fault.
            void me()
              .then((r) => {
                if (r?.user) {
                  end({ kind: 'ready' });
                  void refreshRef.current?.().catch(() => undefined);
                  return;
                }
                const aged = Date.now() - pending.startedAt >= REQUEST_TTL_MS;
                reportFailure(aged ? 'bot_login_expired' : 'bot_login_invalid');
                end({ kind: 'notice', notice: 'expired' });
              })
              .catch(() => {
                reportFailure('bot_login_invalid');
                end({ kind: 'notice', notice: 'expired' });
              });
            return;
          }
          // timeout | expired (fifteen minutes and no Start). A fresh request
          // is prepared under the notice at once.
          reportFailure(`bot_login_${how}`);
          end({ kind: 'notice', notice: 'expired' });
        },
      },
      // Picked up on a page load: ask at once, the person is looking now.
      restored ? { firstPollMs: 0 } : {},
    );
    return () => {
      stop();
      // Unmounted mid-wait, the request stays in the cookie and the resumer —
      // told by this release — collects it.
      releaseBotLoginWait(pending.token, owner);
    };
    // `pending` identity is the whole key: a new tap is a new wait. Everything
    // else the loop calls reads refs or stable setters.
  }, [pending]);

  const countdown = useCountdown(
    phase.kind === 'waiting' && desktop ? phase.pending.expiresAt : null,
  );

  // ── Render ──────────────────────────────────────────────────────────────
  const busy =
    phase.kind === 'completing' ||
    ((phase.kind === 'ready' || phase.kind === 'notice') && prepared.state.status === 'preparing');

  const label = children ?? (
    <span className="inline-flex h-11 w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-gradient-to-b from-telegram to-telegram-deep px-4 text-[14px] font-bold text-white shadow-m-sm">
      <TelegramIcon className="h-[18px] w-[18px] shrink-0" />
      {t('telegram.bot.cta')}
    </span>
  );

  const waitingText = desktop
    ? t('telegram.bot.waitingDesktop')
    : t(authSurface ? 'telegram.bot.waiting' : 'telegram.bot.waitingConnect');
  const hintText =
    inApp.app === 'instagram' || inApp.app === 'facebook'
      ? t('telegram.bot.inAppHint', { app: inApp.app === 'instagram' ? 'Instagram' : 'Facebook' })
      : t('telegram.bot.notOpenedHint');

  let line: ReactNode = null;
  if (phase.kind === 'waiting') {
    line = (
      <div className="mt-2 flex flex-col items-center gap-1 text-center" aria-live="polite">
        {/* The spinner rides INSIDE the text flow and the lines are balanced:
            as a flex row, a two-line wrap on a 390px phone left the spinner
            pinned to the far left and «сюда» alone on the second line. */}
        <p className="text-balance text-[12.5px] leading-snug text-ink-soft">
          <span
            aria-hidden
            className="mr-1.5 inline-block h-3 w-3 animate-spin rounded-full border-2 border-edge border-t-telegram align-[-1px]"
          />
          {waitingText}
          {countdown !== null && (
            <span className="ml-1 tabular-nums text-ink-muted" data-testid="tg-bot-countdown">
              {countdown}
            </span>
          )}
        </p>
        <button
          type="button"
          onClick={() => handOff(phase.pending)}
          className="text-[12px] font-semibold text-ink-muted underline underline-offset-2 transition-colors hover:text-ink"
        >
          {t('telegram.bot.reopen')}
        </button>
        {phase.hint && (
          <>
            <p className="mt-1 text-balance text-[12px] leading-snug text-ink-muted">{hintText}</p>
            <button
              type="button"
              onClick={() => void copyLink(phase.pending)}
              className="text-[12px] font-bold text-brand underline underline-offset-2 transition-colors hover:text-brand-mid"
            >
              {copied ? t('telegram.bot.copied') : t('telegram.bot.copyLink')}
            </button>
          </>
        )}
      </div>
    );
  } else if (phase.kind === 'completing' && !authSurface) {
    // «Подключить Telegram» went through: say so while the host re-reads the
    // session and moves on (the step turns done, the card goes away).
    line = (
      <p className="mt-2 text-center text-[12px] font-semibold text-brand" role="status">
        {t('telegram.bot.connected')}
      </p>
    );
  } else if (phase.kind === 'notice' || prepared.state.status === 'failed') {
    const key =
      prepared.state.status === 'failed'
        ? 'telegram.bot.failed'
        : phase.kind === 'notice'
          ? `telegram.bot.${phase.notice}`
          : null;
    if (key) {
      line = (
        <p className="mt-2 text-center text-[12px] leading-snug text-ink-muted" role="status">
          {t(key)}
        </p>
      );
    }
  }

  return (
    <div className={`flex max-w-full flex-col ${className ?? ''}`}>
      <button
        type="button"
        data-testid="telegram-bot-login"
        onClick={onTap}
        disabled={busy}
        aria-busy={busy}
        className={`relative inline-flex max-w-full text-left ${busy ? 'cursor-wait opacity-60' : ''}`}
      >
        {label}
      </button>
      {line}
    </div>
  );
}
